/**
 * Which bills a patient sees, and in what order.
 *
 * Extracted from LinkPayment / PaymentAccordion for the same reason installmentPlan.ts was: the
 * rules are small, they decide what a patient is shown about their own money, and they are far
 * easier to pin with tests out here than buried in a component that needs a Payabli script and a
 * verified session to render.
 */

export type BillSortKey = 'account' | 'date' | 'amount';

/** Only the fields these rules read. Callers pass richer objects; the generics preserve them. */
export interface BillRow {
  invoiceNumber?: string;
  statementDate?: string;
  totalDueAmount?: number;
}

/**
 * Rows worth showing a patient: anything whose balance is not exactly zero.
 *
 * Reads `totalDueAmount` alone, deliberately, because that is the field the Amount column renders.
 * A row with it `undefined` displays as "$0.00", so it belongs in the hidden set too.
 *
 * The test is `!== 0` and NOT `> 0`. A negative balance is a credit, money the hospital owes the
 * patient, and it must stay visible: hiding it would look like we were concealing it. `> 0` reads
 * more natural and is the obvious tidy-up, which is exactly why billsTable.test.ts pins this.
 */
export function visibleBills<T extends BillRow>(rows: T[]): T[] {
  return (rows ?? []).filter((r) => (r?.totalDueAmount ?? 0) !== 0);
}

/**
 * Sort value for the date column, using the SAME parse the cell renders with (`new Date` /
 * `Date.parse`), so the order always agrees with the dates on screen.
 *
 * Sorting the formatted "Feb 7, 2023" text instead would order alphabetically and put April before
 * January. The NaN guard matters too: without it an absent or unparseable date makes the comparator
 * return NaN, and a comparator that returns NaN leaves the sort order undefined.
 */
export function dateSortValue(raw?: string): number {
  const t = Date.parse(String(raw ?? ''));
  return Number.isNaN(t) ? 0 : t;
}

/**
 * Sorts a COPY. Mutating the caller's array would reorder the parent's list as a side effect.
 *
 * Account numbers use numeric collation so they order by their digits rather than character by
 * character, which starts to matter once they differ in length.
 */
export function sortBills<T extends BillRow>(rows: T[], key: BillSortKey, asc: boolean): T[] {
  const out = [...(rows ?? [])];
  out.sort((a, b) => {
    let cmp: number;
    if (key === 'amount') {
      cmp = (a?.totalDueAmount ?? 0) - (b?.totalDueAmount ?? 0);
    } else if (key === 'date') {
      cmp = dateSortValue(a?.statementDate) - dateSortValue(b?.statementDate);
    } else {
      cmp = String(a?.invoiceNumber ?? '').localeCompare(String(b?.invoiceNumber ?? ''), undefined, {
        numeric: true,
      });
    }
    return asc ? cmp : -cmp;
  });
  return out;
}

/** aria-sort for a header, so the direction is not conveyed by the caret alone. */
export function ariaSortFor(
  active: BillSortKey,
  column: BillSortKey,
  asc: boolean
): 'ascending' | 'descending' | 'none' {
  if (active !== column) return 'none';
  return asc ? 'ascending' : 'descending';
}
